<script setup>
    import { ref, watch, computed } from 'vue'
    import Modal from '@/Components/Modal.vue'
    import { Link, usePage } from '@inertiajs/vue3'
    const props = defineProps({
        show: {
            type: Boolean,
            required: true,
        },
        isLogged: {
            type: Boolean,
            required: false,
        },
        characterId: {
            type: Number,
            default: -1,
        },
        isCustomCharacter: {
            type: Boolean,
            default: false,
        },
    })

    const emit = defineEmits(['close'])
    const page = usePage()

    // Compute backref from current URL
    const backref = computed(() => {
        return typeof window !== 'undefined'
            ? window.location.pathname + window.location.search
            : page.url
    })

    const aiCreditButtonsArray = ref([])

    const fetchAiCreditButtons = () => {
        let aiCreditButtonsRoute = route('billing.aiCreditButtons')
        if (props.characterId !== -1) {
            aiCreditButtonsRoute = route('billing.aiCreditButtons', {
                character_id: props.characterId,
                is_custom_character: props.isCustomCharacter ? 'true' : 'false',
            })
        }

        axios
            .get(aiCreditButtonsRoute)
            .then(response => {
                const data = response.data
                aiCreditButtonsArray.value = Object.keys(data).map(key => data[key])
            })
            .catch(error => {
                console.error('Error fetching AI credit buttons:', error)
            })
    }

    fetchAiCreditButtons()

    const idPopular = computed(() => {
        // Find package with $19.99 or the popular one
        const package1999 = aiCreditButtonsArray.value.find(
            pkg => pkg.amount === '$19.99' || pkg.amount === '19.99',
        )
        return package1999?.id || aiCreditButtonsArray.value.find(pkg => pkg.is_popular)?.id
    })

    const selectedPackage = ref(null)
    const isProcessing = ref(false)

    // Initialize selectedPackage when packages are loaded
    watch(
        () => aiCreditButtonsArray.value.length,
        () => {
            if (aiCreditButtonsArray.value.length > 0 && idPopular.value) {
                selectedPackage.value = idPopular.value
            }
        },
        { immediate: true },
    )

    // Reset to popular/$19.99 package when modal opens
    watch(
        () => props.show,
        isOpen => {
            if (isOpen && idPopular.value) {
                selectedPackage.value = idPopular.value
            }
        },
    )

    const handlePurchase = async () => {
        isProcessing.value = true
        const pkg = aiCreditButtonsArray.value.find(pkg => pkg.id === selectedPackage.value)

        // Open in new tab
        window.open(props.isLogged ? pkg.route : pkg.guest_route, '_blank')

        setTimeout(() => {
            isProcessing.value = false
            emit('close')
        }, 500)
    }

    const closeModal = () => {
        emit('close')
    }
</script>

<template>
    <Modal
        :show="show"
        @close="closeModal"
        max-width="lg"
        class-name="!bg-[#0a0a0a] border border-white/10 !rounded-3xl text-white p-0 rounded-3xl shadow-[0_25px_50px_-12px_rgba(0,0,0,0.25)] max-h-[95vh] overflow-y-auto">
        <div class="relative">
            <!-- Close button -->
            <button
                @click="closeModal"
                class="absolute right-4 top-4 z-10 rounded-full p-1.5 transition-colors hover:bg-white/5"
                aria-label="Close">
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="h-6 w-6 stroke-[3] text-white sm:h-5 sm:w-5">
                    <path d="M18 6 6 18"></path>
                    <path d="m6 6 12 12"></path>
                </svg>
            </button>

            <!-- Header - Minimal -->
            <div class="px-6 pb-6 pt-8 text-center">
                <h2 class="mb-1 text-2xl font-bold text-white">Get FYF Credits</h2>
                <p class="text-sm text-neutral-500">Choose your package</p>
            </div>

            <!-- Package Cards - Compact 3-column grid optimized for mobile -->
            <div class="px-2 pb-6 lg:px-6">
                <div class="grid grid-cols-3 gap-2">
                    <button
                        v-for="pkg in aiCreditButtonsArray"
                        :key="pkg.id"
                        @click="selectedPackage = pkg.id"
                        :class="[
                            'relative rounded-2xl p-3 transition-all',
                            selectedPackage === pkg.id
                                ? 'border-2 border-[#65e8a4] bg-gradient-to-b from-[#65e8a4]/20 to-[#4bc990]/10'
                                : 'border border-[#1a1a1a] bg-[#0f0f0f] hover:border-[#2a2a2a]',
                        ]">
                        <!-- Popular Badge - Compact -->
                        <div
                            v-if="pkg.is_popular"
                            class="absolute -top-1.5 left-1/2 z-10 -translate-x-1/2">
                            <div
                                class="whitespace-nowrap rounded-full bg-[#ff1493] px-3 py-0.5 text-[8px] font-bold capitalize tracking-wide text-white">
                                Popular
                            </div>
                        </div>

                        <!-- Icon -->
                        <div class="mb-2 flex justify-center">
                            <div class="flex h-12 w-12 items-center justify-center">
                                <img
                                    src="https://aws-lg.naughtycdn.com/mediaAssets/65028/FC_token.png"
                                    alt="Fantasy Credits"
                                    :class="[
                                        'h-full w-full object-contain transition-all',
                                        selectedPackage === pkg.id
                                            ? 'drop-shadow-[0_0_8px_rgba(101,232,164,0.6)]'
                                            : 'opacity-70',
                                    ]" />
                            </div>
                        </div>

                        <!-- Token Amount -->
                        <div class="mb-2 text-center">
                            <div class="text-lg font-bold leading-tight text-white">
                                {{ pkg.description }}
                            </div>
                            <div
                                v-if="pkg.bonus_tokens > 0"
                                class="mt-1 text-[9px] font-bold uppercase tracking-wide text-[#65e8a4]">
                                +{{ pkg.bonus_tokens }} Bonus
                            </div>
                        </div>

                        <!-- Price -->
                        <div class="text-center">
                            <div class="mb-0.5 text-[10px] leading-tight text-[#A3A3A3]">
                                {{ pkg.pricePerToken }}
                            </div>
                            <div class="text-base font-bold text-white">
                                {{ pkg.amount }}
                            </div>
                        </div>

                        <!-- Selected Indicator -->
                        <div v-if="selectedPackage === pkg.id" class="absolute right-1.5 top-1.5">
                            <div
                                class="flex h-4 w-4 items-center justify-center rounded-full bg-[#65e8a4]">
                                <svg
                                    class="h-2.5 w-2.5 text-black"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24">
                                    <path
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        stroke-width="3"
                                        d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                        </div>
                    </button>
                </div>
            </div>

            <!-- Purchase Section -->
            <div class="px-6 pb-6">
                <button
                    @click="handlePurchase"
                    :disabled="isProcessing"
                    class="w-full rounded-full bg-white px-6 py-3.5 text-[14px] font-semibold text-black shadow-lg transition-all hover:scale-[1.02] hover:bg-gray-200 hover:shadow-xl disabled:cursor-not-allowed disabled:bg-[#1a1a1a] disabled:text-gray-600">
                    <span v-if="isProcessing" class="flex items-center justify-center gap-2">
                        <div
                            class="h-4 w-4 animate-spin rounded-full border-2 border-black border-t-transparent"></div>
                        Processing...
                    </span>
                    <span v-else>Buy FYF Credits</span>
                </button>

                <p class="mt-3 text-center text-[11px] font-normal leading-relaxed text-[#A0A0A0]">
                    By pressing "Buy now", you accept our Terms and authorize this purchase
                </p>
            </div>

            <!-- Usage Info - What you can do -->
            <div class="px-6 pb-6">
                <div class="rounded-xl border border-[#1a1a1a] bg-[#0f0f0f] p-4">
                    <h4 class="mb-3 text-center text-sm font-semibold text-[#B3B3B3]">
                        Enhance your experience with FYF credits
                    </h4>
                    <div class="space-y-2 text-sm text-neutral-400">
                        <div class="flex items-center gap-2">
                            <div class="h-1 w-1 flex-shrink-0 rounded-full bg-[#65e8a4]"></div>
                            <span>DM creators directly</span>
                        </div>
                        <div class="flex items-center gap-2">
                            <div class="h-1 w-1 flex-shrink-0 rounded-full bg-[#65e8a4]"></div>
                            <span>Request custom personalized content</span>
                        </div>
                        <div class="flex items-center gap-2">
                            <div class="h-1 w-1 flex-shrink-0 rounded-full bg-[#65e8a4]"></div>
                            <span>Unlock exclusive photos & videos</span>
                        </div>
                        <div class="flex items-center gap-2">
                            <div class="h-1 w-1 flex-shrink-0 rounded-full bg-[#65e8a4]"></div>
                            <span>Show your appreciation</span>
                        </div>
                        <div class="flex items-center gap-2">
                            <div class="h-1 w-1 flex-shrink-0 rounded-full bg-[#65e8a4]"></div>
                            <span>Access VIP features</span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Footer Links - Minimal -->
            <div class="px-6 pb-6">
                <div class="text-center">
                    <Link
                        :href="route('profile.paymentMethod', { backref: backref })"
                        class="text-sm text-neutral-400 underline transition-colors hover:text-neutral-300">
                        Manage my payment info
                    </Link>
                </div>
            </div>
        </div>
    </Modal>
</template>
